/**
 * latexToDocxMath — minimal, dependency-free LaTeX → docx Math converter.
 *
 * Why hand-rolled: every published MathML→OMML converter is LGPL (license
 * risk when bundled) or Node-only (mathjax-node/jsdom). The docx lib ships
 * native OMML components, so we translate the common LaTeX subset directly:
 *
 *   - plain symbols/numbers/operators, {…} groups
 *   - ^ and _ scripts (either order)                → MathSuperScript / MathSubScript / MathSubSuperScript
 *   - \frac{a}{b}                                   → MathFraction
 *   - \sqrt{x} and \sqrt[n]{x}                      → MathRadical
 *   - \sum / \int / \prod with optional limits      → MathSum / MathIntegral (∏ via scripts)
 *   - \left( … \right), \text{…}, spacing commands
 *   - Greek letters and common symbol commands      → unicode MathRuns
 *
 * Anything outside the subset throws internally and `latexToDocxMath`
 * returns null — callers fall back to the legacy italic `$latex$` text so
 * exports never break. The OOXML import side (mathParser.ts) converts all
 * of these constructs back to LaTeX, completing the round-trip.
 */

import {
  Math as DocxMath,
  MathRun,
  MathFraction,
  MathRadical,
  MathSubScript,
  MathSuperScript,
  MathSubSuperScript,
  MathSum,
  MathIntegral,
} from "docx";

// The docx lib doesn't export a MathComponent union type usable here;
// all Math* classes are XmlComponents, so we treat children loosely.
type MathChild = any;

const SYMBOLS: Record<string, string> = {
  // Greek (lowercase)
  alpha: "α",
  beta: "β",
  gamma: "γ",
  delta: "δ",
  epsilon: "ε",
  varepsilon: "ε",
  zeta: "ζ",
  eta: "η",
  theta: "θ",
  vartheta: "ϑ",
  iota: "ι",
  kappa: "κ",
  lambda: "λ",
  mu: "μ",
  nu: "ν",
  xi: "ξ",
  pi: "π",
  rho: "ρ",
  sigma: "σ",
  tau: "τ",
  upsilon: "υ",
  phi: "φ",
  varphi: "ϕ",
  chi: "χ",
  psi: "ψ",
  omega: "ω",
  // Greek (uppercase)
  Gamma: "Γ",
  Delta: "Δ",
  Theta: "Θ",
  Lambda: "Λ",
  Xi: "Ξ",
  Pi: "Π",
  Sigma: "Σ",
  Upsilon: "Υ",
  Phi: "Φ",
  Psi: "Ψ",
  Omega: "Ω",
  // Operators / relations
  pm: "±",
  mp: "∓",
  times: "×",
  div: "÷",
  cdot: "⋅",
  ast: "∗",
  leq: "≤",
  le: "≤",
  geq: "≥",
  ge: "≥",
  neq: "≠",
  ne: "≠",
  approx: "≈",
  equiv: "≡",
  sim: "∼",
  propto: "∝",
  ll: "≪",
  gg: "≫",
  subset: "⊂",
  supset: "⊃",
  subseteq: "⊆",
  supseteq: "⊇",
  in: "∈",
  notin: "∉",
  cup: "∪",
  cap: "∩",
  emptyset: "∅",
  forall: "∀",
  exists: "∃",
  // Arrows
  rightarrow: "→",
  to: "→",
  leftarrow: "←",
  leftrightarrow: "↔",
  Rightarrow: "⇒",
  Leftarrow: "⇐",
  Leftrightarrow: "⇔",
  mapsto: "↦",
  // Misc
  infty: "∞",
  partial: "∂",
  nabla: "∇",
  degree: "°",
  prime: "′",
  hbar: "ℏ",
  ell: "ℓ",
  Re: "ℜ",
  Im: "ℑ",
  aleph: "ℵ",
  angle: "∠",
  perp: "⊥",
  parallel: "∥",
  therefore: "∴",
  because: "∵",
  ldots: "…",
  cdots: "⋯",
  dots: "…",
};

const SPACING_COMMANDS = new Set([",", ";", ":", "!", "quad", "qquad", " "]);

const FUNCTION_NAMES = new Set([
  "sin",
  "cos",
  "tan",
  "cot",
  "sec",
  "csc",
  "arcsin",
  "arccos",
  "arctan",
  "sinh",
  "cosh",
  "tanh",
  "log",
  "ln",
  "lg",
  "exp",
  "min",
  "max",
  "lim",
  "det",
  "dim",
  "mod",
  "gcd",
  "arg",
  "deg",
]);

class UnsupportedLatexError extends Error {}

// ---------------------------------------------------------------------------
// Tokenizer
// ---------------------------------------------------------------------------

type Token =
  | { type: "cmd"; name: string }
  | { type: "char"; value: string }
  | { type: "open" }
  | { type: "close" }
  | { type: "sup" }
  | { type: "sub" };

function tokenize(latex: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  while (i < latex.length) {
    const ch = latex[i];
    if (ch === "\\") {
      // Command: letters, or a single non-letter (e.g. "\," "\{")
      let j = i + 1;
      if (j < latex.length && /[a-zA-Z]/.test(latex[j])) {
        while (j < latex.length && /[a-zA-Z]/.test(latex[j])) j++;
        tokens.push({ type: "cmd", name: latex.slice(i + 1, j) });
        i = j;
      } else {
        tokens.push({ type: "cmd", name: latex[j] ?? "" });
        i = j + 1;
      }
    } else if (ch === "{") {
      tokens.push({ type: "open" });
      i++;
    } else if (ch === "}") {
      tokens.push({ type: "close" });
      i++;
    } else if (ch === "^") {
      tokens.push({ type: "sup" });
      i++;
    } else if (ch === "_") {
      tokens.push({ type: "sub" });
      i++;
    } else if (ch === " " || ch === "\n" || ch === "\t") {
      i++; // insignificant whitespace
    } else {
      tokens.push({ type: "char", value: ch });
      i++;
    }
  }
  return tokens;
}

// ---------------------------------------------------------------------------
// Parser
// ---------------------------------------------------------------------------

class Parser {
  private pos = 0;

  constructor(private readonly tokens: Token[]) {}

  private peek(): Token | undefined {
    return this.tokens[this.pos];
  }

  private next(): Token | undefined {
    return this.tokens[this.pos++];
  }

  /** Parse until end of tokens or a closing brace (which is consumed). */
  parseSequence(stopAtClose: boolean): MathChild[] {
    const out: MathChild[] = [];
    let pending = ""; // consecutive plain characters buffered into one run

    const flush = (keepLast: boolean): string => {
      // Returns the detached last char when keepLast, after emitting the rest
      let base = "";
      if (keepLast && pending.length > 0) {
        base = pending.slice(-1);
        pending = pending.slice(0, -1);
      }
      if (pending) out.push(new MathRun(pending));
      pending = "";
      return base;
    };

    for (;;) {
      const token = this.peek();
      if (!token) {
        if (stopAtClose) throw new UnsupportedLatexError("Unbalanced braces");
        break;
      }

      if (token.type === "close") {
        if (!stopAtClose) throw new UnsupportedLatexError("Unbalanced braces");
        this.next();
        break;
      }

      if (token.type === "char") {
        this.next();
        pending += token.value;
        continue;
      }

      if (token.type === "sup" || token.type === "sub") {
        // Script binds to the immediately preceding atom
        const baseChar = flush(true);
        let base: MathChild[];
        if (baseChar) {
          base = [new MathRun(baseChar)];
        } else if (out.length > 0) {
          base = [out.pop()];
        } else {
          throw new UnsupportedLatexError("Script with no base");
        }
        out.push(this.parseScripts(base));
        continue;
      }

      if (token.type === "open") {
        flush(false);
        this.next();
        const group = this.parseSequence(true);
        // A bare group contributes its children inline
        out.push(...group);
        continue;
      }

      // Command
      this.next();
      const component = this.parseCommand(token.name, out, flush);
      if (component !== null) {
        out.push(...component);
      }
    }

    flush(false);
    return out;
  }

  /** Consume ^/_ arguments (either order, at most one each) for a base. */
  private parseScripts(base: MathChild[]): MathChild {
    let sup: MathChild[] | null = null;
    let sub: MathChild[] | null = null;
    for (let k = 0; k < 2; k++) {
      const token = this.peek();
      if (token?.type === "sup" && !sup) {
        this.next();
        sup = this.parseArgument();
      } else if (token?.type === "sub" && !sub) {
        this.next();
        sub = this.parseArgument();
      } else {
        break;
      }
    }
    if (sup && sub) {
      return new MathSubSuperScript({
        children: base,
        superScript: sup,
        subScript: sub,
      });
    }
    if (sup) return new MathSuperScript({ children: base, superScript: sup });
    if (sub) return new MathSubScript({ children: base, subScript: sub });
    // ^ or _ with nothing parsed — shouldn't happen
    throw new UnsupportedLatexError("Dangling script");
  }

  /** A single argument: {…} group, a command, or one character. */
  private parseArgument(): MathChild[] {
    const token = this.next();
    if (!token) throw new UnsupportedLatexError("Missing argument");
    if (token.type === "open") {
      return this.parseSequence(true);
    }
    if (token.type === "char") {
      return [new MathRun(token.value)];
    }
    if (token.type === "cmd") {
      const out: MathChild[] = [];
      const flushNoop = () => "";
      const parts = this.parseCommand(token.name, out, flushNoop);
      if (parts === null || (parts.length === 0 && out.length === 0)) {
        throw new UnsupportedLatexError(`Empty argument: \\${token.name}`);
      }
      return [...out, ...(parts || [])];
    }
    throw new UnsupportedLatexError("Bad argument");
  }

  /**
   * Translate one command. May consume further tokens (arguments).
   * Returns components to append, or null when the command only produced
   * side effects (e.g. spacing).
   */
  private parseCommand(
    name: string,
    out: MathChild[],
    flush: (keepLast: boolean) => string
  ): MathChild[] | null {
    if (SPACING_COMMANDS.has(name)) {
      flush(false);
      return [new MathRun(" ")];
    }

    if (name in SYMBOLS) {
      flush(false);
      return [new MathRun(SYMBOLS[name])];
    }

    if (FUNCTION_NAMES.has(name)) {
      flush(false);
      return [new MathRun(name)];
    }

    switch (name) {
      case "frac":
      case "dfrac":
      case "tfrac": {
        flush(false);
        const numerator = this.parseArgument();
        const denominator = this.parseArgument();
        return [new MathFraction({ numerator, denominator })];
      }

      case "sqrt": {
        flush(false);
        // Optional [degree]
        let degree: MathChild[] | undefined;
        const token = this.peek();
        if (token?.type === "char" && token.value === "[") {
          this.next();
          let deg = "";
          for (;;) {
            const t = this.next();
            if (!t) throw new UnsupportedLatexError("Unclosed sqrt degree");
            if (t.type === "char" && t.value === "]") break;
            if (t.type === "char") deg += t.value;
            else throw new UnsupportedLatexError("Complex sqrt degree");
          }
          degree = [new MathRun(deg)];
        }
        const children = this.parseArgument();
        return [new MathRadical({ children, degree })];
      }

      case "sum":
      case "int":
      case "prod": {
        flush(false);
        // Optional limits in either order
        let sup: MathChild[] | undefined;
        let sub: MathChild[] | undefined;
        for (let k = 0; k < 2; k++) {
          const t = this.peek();
          if (t?.type === "sub" && !sub) {
            this.next();
            sub = this.parseArgument();
          } else if (t?.type === "sup" && !sup) {
            this.next();
            sup = this.parseArgument();
          } else {
            break;
          }
        }
        // Operand: the next primary expression (group/char/command + its scripts)
        const operand = this.parseOperand();
        if (name === "sum") {
          return [
            new MathSum({
              children: operand,
              subScript: sub,
              superScript: sup,
            }),
          ];
        }
        if (name === "int") {
          return [
            new MathIntegral({
              children: operand,
              subScript: sub,
              superScript: sup,
            }),
          ];
        }
        // \prod — docx has no product n-ary; render ∏ with scripts
        const prodBase: MathChild[] = [new MathRun("∏")];
        let prodComponent: MathChild;
        if (sup && sub) {
          prodComponent = new MathSubSuperScript({
            children: prodBase,
            superScript: sup,
            subScript: sub,
          });
        } else if (sup) {
          prodComponent = new MathSuperScript({
            children: prodBase,
            superScript: sup,
          });
        } else if (sub) {
          prodComponent = new MathSubScript({
            children: prodBase,
            subScript: sub,
          });
        } else {
          prodComponent = prodBase[0];
        }
        return [prodComponent, ...operand];
      }

      case "left":
      case "right": {
        // Treat the delimiter that follows as a literal character
        flush(false);
        const t = this.next();
        if (!t) throw new UnsupportedLatexError("Dangling \\left/\\right");
        if (t.type === "char") {
          if (t.value === ".") return []; // invisible delimiter
          return [new MathRun(t.value)];
        }
        if (t.type === "cmd" && t.name in SYMBOLS) {
          return [new MathRun(SYMBOLS[t.name])];
        }
        if (t.type === "cmd" && (t.name === "{" || t.name === "}")) {
          return [new MathRun(t.name)];
        }
        throw new UnsupportedLatexError("Unsupported \\left/\\right delimiter");
      }

      case "text":
      case "mathrm":
      case "operatorname": {
        flush(false);
        const token = this.next();
        if (token?.type !== "open") {
          throw new UnsupportedLatexError(`\\${name} requires a group`);
        }
        let text = "";
        for (;;) {
          const t = this.next();
          if (!t) throw new UnsupportedLatexError(`Unclosed \\${name}`);
          if (t.type === "close") break;
          if (t.type === "char") text += t.value;
          else if (t.type === "cmd" && SPACING_COMMANDS.has(t.name))
            text += " ";
          else throw new UnsupportedLatexError(`Complex \\${name} content`);
        }
        return [new MathRun(text)];
      }

      case "{":
      case "}":
      case "%":
      case "&":
      case "#":
      case "$":
        flush(false);
        return [new MathRun(name)];

      default:
        throw new UnsupportedLatexError(`Unsupported command: \\${name}`);
    }
  }

  /** One primary expression with optional trailing scripts (n-ary operand). */
  private parseOperand(): MathChild[] {
    const token = this.peek();
    if (!token || token.type === "close") {
      return [new MathRun("")];
    }
    let base: MathChild[];
    if (token.type === "open") {
      this.next();
      base = this.parseSequence(true);
    } else if (token.type === "char") {
      this.next();
      base = [new MathRun(token.value)];
    } else if (token.type === "cmd") {
      this.next();
      const out: MathChild[] = [];
      const parts = this.parseCommand(token.name, out, () => "");
      base = [...out, ...(parts || [])];
    } else {
      return [new MathRun("")];
    }
    // Trailing scripts bind to the operand (e.g. \sum_{i=1}^{n} x_i)
    const t = this.peek();
    if (t?.type === "sup" || t?.type === "sub") {
      return [this.parseScripts(base)];
    }
    return base;
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Convert a LaTeX string to docx Math children.
 * @throws UnsupportedLatexError when the input uses constructs outside the subset
 */
export function latexToMathChildren(latex: string): MathChild[] {
  const tokens = tokenize(latex);
  if (tokens.length === 0) throw new UnsupportedLatexError("Empty formula");
  const children = new Parser(tokens).parseSequence(false);
  if (children.length === 0) throw new UnsupportedLatexError("Empty formula");
  return children;
}

/**
 * Convert a LaTeX string to a docx Math component, or null when the
 * formula uses unsupported constructs (callers should fall back to
 * plain-text rendering).
 */
export function latexToDocxMath(
  latex: string
): InstanceType<typeof DocxMath> | null {
  try {
    return new DocxMath({ children: latexToMathChildren(latex) });
  } catch {
    return null;
  }
}
